import { Link } from "react-router-dom";
import { CheckCircle, XCircle } from "lucide-react";

export const About = () => {
  return (
    <div style={{
      maxWidth: "780px",
      margin: "0 auto",
      padding: "4rem 2rem"
    }}>
          {/* Page Title */}
          <h1 style={{
            fontSize: "2.25rem",
            fontWeight: "800",
            color: "#111827",
            marginBottom: "1rem",
            lineHeight: "1.2"
          }}>
            About SHOWME Campaign Finances
          </h1>

          {/* Key Promise Callout */}
          <div style={{
            background: "linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%)",
            border: "1px solid #bfdbfe",
            borderRadius: "10px",
            padding: "1.5rem",
            marginBottom: "3rem",
            textAlign: "center"
          }}>
            <p style={{
              fontSize: "1.15rem",
              fontWeight: "600",
              color: "#1e40af",
              margin: 0,
              lineHeight: "1.6"
            }}>
              Convert MEC PDFs into clean spreadsheets in seconds.
            </p>
          </div>

          {/* What This Is */}
          <section style={{ marginBottom: "3rem" }}>
            <h2 style={{
              fontSize: "1.5rem",
              fontWeight: "700",
              color: "#111827",
              marginBottom: "1rem"
            }}>
              What This Is
            </h2>
            <p style={{
              color: "#4b5563",
              lineHeight: "1.7",
              marginBottom: "1rem",
              fontSize: "1.05rem"
            }}>
              SHOWME Campaign Finances is a tool that converts Missouri Ethics Commission (MEC) campaign finance PDF reports into clean, structured spreadsheets.
            </p>
            <p style={{
              color: "#4b5563",
              lineHeight: "1.7",
              marginBottom: "1rem",
              fontSize: "1.05rem"
            }}>
              Campaign finance data is public, but it is often locked inside PDFs that are time-consuming to work with. SHOWME removes that friction by turning one or many MEC filings into analysis-ready CSV or Excel files in seconds.
            </p>
            <p style={{
              color: "#4b5563",
              lineHeight: "1.7",
              fontSize: "1.05rem"
            }}>
              The goal is simple: make public campaign finance data usable.
            </p>
            <p style={{
              color: "#4b5563",
              lineHeight: "1.7",
              fontSize: "1.05rem",
              marginTop: "1rem"
            }}>
              SHOWME works exclusively with publicly available Missouri Ethics Commission filings and does not alter the underlying data.
            </p>
          </section>

          {/* Who It's For */}
          <section style={{ marginBottom: "3rem" }}>
            <h2 style={{
              fontSize: "1.5rem",
              fontWeight: "700",
              color: "#111827",
              marginBottom: "1rem"
            }}>
              Who It's For
            </h2>
            <p style={{
              color: "#4b5563",
              lineHeight: "1.7",
              marginBottom: "1rem",
              fontSize: "1.05rem"
            }}>
              SHOWME is designed for anyone who needs to work with Missouri campaign finance data, including:
            </p>
            <ul style={{
              color: "#4b5563",
              lineHeight: "1.8",
              marginBottom: "1rem",
              fontSize: "1.05rem",
              paddingLeft: "1.5rem"
            }}>
              <li>Journalists and researchers</li>
              <li>Advocacy and watchdog groups</li>
              <li>Campaigns and consultants</li>
              <li>Civic organizations</li>
              <li>Members of the public who want transparency without manual work</li>
            </ul>
            <p style={{
              color: "#4b5563",
              lineHeight: "1.7",
              fontSize: "1.05rem"
            }}>
              If you've ever copied rows out of a PDF into a spreadsheet, this tool is for you.
            </p>
            <p style={{
              color: "#4b5563",
              lineHeight: "1.7",
              fontSize: "1.05rem",
              marginTop: "1rem"
            }}>
              SHOWME is used by people who need accuracy and repeatability, not opinions.
            </p>
          </section>

          {/* What SHOWME Does / Is Not - Side by Side Cards */}
          <section style={{ marginBottom: "3rem" }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "2rem",
              marginBottom: "2rem"
            }}>
              {/* What SHOWME Does */}
              <div style={{
                background: "white",
                border: "2px solid #d1fae5",
                borderRadius: "10px",
                padding: "1.5rem",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)"
              }}>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  marginBottom: "1rem"
                }}>
                  <CheckCircle size={24} style={{ color: "#10b981", flexShrink: 0 }} />
                  <h3 style={{
                    fontSize: "1.25rem",
                    fontWeight: "700",
                    color: "#111827",
                    margin: 0
                  }}>
                    What SHOWME Does
                  </h3>
                </div>
                <ul style={{
                  color: "#4b5563",
                  lineHeight: "1.7",
                  fontSize: "0.95rem",
                  paddingLeft: "1.25rem",
                  margin: 0
                }}>
                  <li>Accepts official Missouri Ethics Commission PDF reports</li>
                  <li>Extracts donations, expenses, dates, payees, and related metadata</li>
                  <li>Outputs clean CSV or Excel spreadsheets</li>
                  <li>Supports single or multiple filings at once</li>
                  <li>Preserves the original data without modification</li>
                </ul>
              </div>

              {/* What SHOWME Is Not */}
              <div style={{
                background: "white",
                border: "2px solid #fee2e2",
                borderRadius: "10px",
                padding: "1.5rem",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)"
              }}>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  marginBottom: "1rem"
                }}>
                  <XCircle size={24} style={{ color: "#ef4444", flexShrink: 0 }} />
                  <h3 style={{
                    fontSize: "1.25rem",
                    fontWeight: "700",
                    color: "#111827",
                    margin: 0
                  }}>
                    What SHOWME Is Not
                  </h3>
                </div>
                <ul style={{
                  color: "#4b5563",
                  lineHeight: "1.7",
                  fontSize: "0.95rem",
                  paddingLeft: "1.25rem",
                  margin: 0
                }}>
                  <li>Not affiliated with or endorsed by the Missouri Ethics Commission</li>
                  <li>Not a source of political analysis, interpretation, or opinion</li>
                  <li>Not a replacement for official filings</li>
                </ul>
                <p style={{
                  color: "#6b7280",
                  fontSize: "0.9rem",
                  marginTop: "1rem",
                  marginBottom: 0,
                  fontStyle: "italic"
                }}>
                  SHOWME is a data preparation tool, not a reporting authority.
                </p>
              </div>
            </div>

            <p style={{
              color: "#4b5563",
              lineHeight: "1.7",
              fontSize: "1.05rem",
              marginTop: "1.5rem"
            }}>
              SHOWME does not alter, estimate, or interpret the data. It focuses solely on accurate extraction and formatting.
            </p>
          </section>

          {/* Why This Exists */}
          <section style={{ marginBottom: "3rem" }}>
            <h2 style={{
              fontSize: "1.5rem",
              fontWeight: "700",
              color: "#111827",
              marginBottom: "1rem"
            }}>
              Why This Exists
            </h2>
            <p style={{
              color: "#4b5563",
              lineHeight: "1.7",
              marginBottom: "1rem",
              fontSize: "1.05rem"
            }}>
              Campaign finance transparency shouldn't require hours of manual transcription.
            </p>
            <p style={{
              color: "#4b5563",
              lineHeight: "1.7",
              fontSize: "1.05rem"
            }}>
              Public records are only useful if people can actually work with them. SHOWME exists to remove unnecessary labor so users can spend their time analyzing, auditing, or reporting — not retyping PDFs.
            </p>
          </section>

          {/* Data Accuracy & Transparency */}
          <section style={{ marginBottom: "3rem" }}>
            <h2 style={{
              fontSize: "1.5rem",
              fontWeight: "700",
              color: "#111827",
              marginBottom: "1rem"
            }}>
              Data Accuracy & Transparency
            </h2>
            <p style={{
              color: "#4b5563",
              lineHeight: "1.7",
              marginBottom: "1rem",
              fontSize: "1.05rem"
            }}>
              SHOWME processes data directly from uploaded MEC reports. While every effort is made to extract information accurately, users should always treat outputs as a convenience layer and refer to original filings for official or legal purposes.
            </p>
            <p style={{
              color: "#4b5563",
              lineHeight: "1.7",
              marginBottom: "1rem",
              fontSize: "1.05rem"
            }}>
              A detailed explanation of data handling and limitations is available on the Methodology page.
            </p>
            <p style={{
              color: "#4b5563",
              lineHeight: "1.7",
              fontSize: "1.05rem"
            }}>
              SHOWME does not replace official filings and should not be relied upon as the sole source for legal or compliance purposes.
            </p>
          </section>

          {/* Contact & Support */}
          <section style={{
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "10px",
            padding: "2rem",
            textAlign: "center"
          }}>
            <h2 style={{
              fontSize: "1.5rem",
              fontWeight: "700",
              color: "#111827",
              marginBottom: "1rem"
            }}>
              Contact & Support
            </h2>
            <p style={{
              color: "#4b5563",
              lineHeight: "1.7",
              marginBottom: "1.5rem",
              fontSize: "1.05rem"
            }}>
              Questions, issues, or feedback are welcome.
            </p>
            <a
              href="mailto:support@showmecampaignfinances.com"
              style={{
                display: "inline-block",
                padding: "0.75rem 1.5rem",
                background: "linear-gradient(90deg, #2563eb, #4f46e5)",
                color: "white",
                borderRadius: "8px",
                fontWeight: "600",
                textDecoration: "none",
                boxShadow: "0 2px 8px rgba(37, 99, 235, 0.2)",
                transition: "opacity 0.2s"
              }}
              onMouseEnter={(e) => e.target.style.opacity = "0.9"}
              onMouseLeave={(e) => e.target.style.opacity = "1"}
            >
              Contact Support
            </a>
          </section>

          {/* Mobile responsive styles */}
          <style>{`
            @media (max-width: 768px) {
              div[style*="gridTemplateColumns: 1fr 1fr"] {
                grid-template-columns: 1fr !important;
              }
            }
          `}</style>
    </div>
  );
};
